// import { createTheme } from "@mui/material/styles";

// const theme = createTheme({
//   palette: {
//     mode: "light",
//     primary: {
//       main: "#1976d2", // wp-spark theme blue
//     },
//     secondary: {
//       main: "#9c27b0",
//     },
//     background: {
//       default: "#f4f6f8",
//     },
//   },
//   typography: {
//     fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
//     h1: { fontSize: "2rem", fontWeight: 600 },
//     h2: { fontSize: "1.5rem", fontWeight: 500 },
//     body1: { fontSize: "1rem" },
//   },
//   shape: {
//     borderRadius: 8,
//   },
// });

// export default theme;

import { createTheme } from "@mui/material/styles";
import {
  BORDER_RADIUS,
  ANIMATION,
  Z_INDEX,
  COMPONENT_SIZES,
} from "./constants";

// Light palette
const lightPalette = {
  mode: "light",
  primary: {
    main: "#0073e6",
    light: "#3d94f0",
    dark: "#0052a3",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#00c9a7",
    light: "#33d4b8",
    dark: "#008c75",
    contrastText: "#ffffff",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  info: {
    main: "#3b82f6",
    light: "#60a5fa",
    dark: "#2563eb",
  },
  background: {
    default: "#f8fafc",
    paper: "#ffffff",
  },
  text: {
    primary: "#1e293b",
    secondary: "#64748b",
    disabled: "#94a3b8",
  },
  divider: "#e2e8f0",
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

// Dark palette
const darkPalette = {
  mode: "dark",
  primary: {
    main: "#3b82f6",
    light: "#60a5fa",
    dark: "#2563eb",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
    contrastText: "#ffffff",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  info: {
    main: "#3b82f6",
    light: "#60a5fa",
    dark: "#2563eb",
  },
  background: {
    default: "#0f172a",
    paper: "#1e293b",
  },
  text: {
    primary: "#f1f5f9",
    secondary: "#94a3b8",
    disabled: "#64748b",
  },
  divider: "#334155",
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

// Create theme with mode parameter
export const createAppTheme = (mode = "light") => {
  const palette = mode === "dark" ? darkPalette : lightPalette;

  return createTheme({
    palette,
    typography: {
      fontFamily: [
        "Inter",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        "sans-serif",
      ].join(","),
      fontSize: 13,
      h1: {
        fontSize: "1.75rem",
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.5,
      },
      subtitle1: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "0.8125rem",
        lineHeight: 1.4,
      },
      button: {
        fontSize: "0.8125rem",
        fontWeight: 500,
        textTransform: "none",
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.4,
      },
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      },
    },
    shape: {
      borderRadius: BORDER_RADIUS.sm,
    },
    shadows: [
      "none",
      "0 1px 2px rgba(0,0,0,0.04)",
      "0 1px 3px rgba(0,0,0,0.06)",
      "0 2px 4px rgba(0,0,0,0.06)",
      "0 2px 6px rgba(0,0,0,0.08)",
      "0 3px 8px rgba(0,0,0,0.08)",
      "0 4px 12px rgba(0,0,0,0.1)",
      "0 6px 16px rgba(0,0,0,0.1)",
      "0 8px 20px rgba(0,0,0,0.12)",
      "0 10px 24px rgba(0,0,0,0.12)",
      "0 12px 28px rgba(0,0,0,0.14)",
      "0 14px 32px rgba(0,0,0,0.14)",
      "0 16px 36px rgba(0,0,0,0.16)",
      "0 18px 40px rgba(0,0,0,0.16)",
      "0 20px 44px rgba(0,0,0,0.18)",
      "0 22px 48px rgba(0,0,0,0.18)",
      "0 24px 52px rgba(0,0,0,0.2)",
      "0 26px 56px rgba(0,0,0,0.2)",
      "0 28px 60px rgba(0,0,0,0.22)",
      "0 30px 64px rgba(0,0,0,0.22)",
      "0 32px 68px rgba(0,0,0,0.24)",
      "0 34px 72px rgba(0,0,0,0.24)",
      "0 36px 76px rgba(0,0,0,0.26)",
      "0 38px 80px rgba(0,0,0,0.26)",
      "0 40px 84px rgba(0,0,0,0.28)",
    ],
    components: {
      MuiButton: {
        defaultProps: {
          size: "small",
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            fontWeight: 500,
            boxShadow: "none",
            transition: "all 0.15s ease",
            "&:hover": {
              boxShadow: "none",
            },
          },
          sizeSmall: {
            height: COMPONENT_SIZES.button.small.height,
            padding: COMPONENT_SIZES.button.small.padding,
            fontSize: "0.75rem",
          },
          sizeMedium: {
            height: COMPONENT_SIZES.button.medium.height,
            padding: COMPONENT_SIZES.button.medium.padding,
            fontSize: "0.8125rem",
          },
          sizeLarge: {
            height: COMPONENT_SIZES.button.large.height,
            padding: COMPONENT_SIZES.button.large.padding,
            fontSize: "0.875rem",
          },
          contained: {
            "&:hover": {
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            },
          },
          outlined: {
            borderWidth: 1,
            "&:hover": {
              borderWidth: 1,
            },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            transition: "all 0.15s ease",
          },
          sizeSmall: {
            padding: 4,
          },
          sizeMedium: {
            padding: 6,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.md,
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            border: mode === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
            transition: "all 0.15s ease",
            "&:hover": {
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 16,
            "&:last-child": {
              paddingBottom: 16,
            },
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: "10px 16px",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.md,
          },
          elevation1: {
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          },
        },
      },
      MuiChip: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: "0.8125rem",
            height: 24,
          },
          sizeSmall: {
            height: 22,
            fontSize: "0.75rem",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: BORDER_RADIUS.sm,
              fontSize: "0.875rem",
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
              },
            },
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            fontSize: "0.875rem",
          },
          input: {
            padding: "8px 12px",
          },
          inputSizeSmall: {
            padding: "8px 12px",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
          },
          sizeSmall: {
            fontSize: "0.875rem",
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          size: "small",
        },
        styleOverrides: {
          select: {
            padding: "8px 12px",
            fontSize: "0.875rem",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            minHeight: 36,
            padding: "6px 14px",
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          size: "small",
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: "0.75rem",
            marginTop: 4,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            padding: "4px 8px",
          },
          option: {
            fontSize: "0.875rem",
            minHeight: 36,
            padding: "6px 14px",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            borderRight:
              mode === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderBottom:
              mode === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: "48px !important",
            padding: "0 12px !important",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "10px 14px",
            fontSize: "0.875rem",
          },
          head: {
            fontWeight: 600,
            fontSize: "0.8125rem",
            backgroundColor: mode === "dark" ? "#1e293b" : "#f8fafc",
            borderBottom:
              mode === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
            padding: "10px 14px",
          },
          sizeSmall: {
            padding: "8px 12px",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-child td": {
              borderBottom: 0,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
          },
          selectLabel: {
            fontSize: "0.75rem",
          },
          displayedRows: {
            fontSize: "0.75rem",
          },
          select: {
            fontSize: "0.8125rem",
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            padding: "6px 12px",
            fontSize: "0.8125rem",
          },
          icon: {
            padding: "4px 0",
            marginRight: 8,
          },
          message: {
            padding: "4px 0",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: BORDER_RADIUS.md,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "1.0625rem",
            fontWeight: 600,
            padding: "14px 20px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "16px 20px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "8px 16px 12px",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: BORDER_RADIUS.sm,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: mode === "dark" ? "1px solid #334155" : "1px solid #e2e8f0",
          },
          list: {
            padding: "4px",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: BORDER_RADIUS.xs,
            fontSize: "0.6875rem",
            padding: "4px 8px",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: "0.75rem",
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            padding: "4px 8px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            padding: "6px 10px",
            minHeight: 36,
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(59, 130, 246, 0.08)"
                  : "rgba(0, 115, 230, 0.06)",
            },
            "&.Mui-selected": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(59, 130, 246, 0.12)"
                  : "rgba(0, 115, 230, 0.1)",
              "&:hover": {
                backgroundColor:
                  mode === "dark"
                    ? "rgba(59, 130, 246, 0.16)"
                    : "rgba(0, 115, 230, 0.14)",
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 32,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: "0.8125rem",
          },
          secondary: {
            fontSize: "0.75rem",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.8125rem",
            minHeight: 40,
            padding: "8px 14px",
            transition: "all 0.15s ease",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          indicator: {
            height: 2,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.xs,
            height: 4,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontSize: "0.625rem",
            height: 16,
            minWidth: 16,
            padding: "0 4px",
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          size: "small",
        },
      },
      MuiCheckbox: {
        defaultProps: {
          size: "small",
        },
      },
      MuiRadio: {
        defaultProps: {
          size: "small",
        },
      },
      MuiSlider: {
        defaultProps: {
          size: "small",
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: 40,
            padding: "0 12px",
            "&.Mui-expanded": {
              minHeight: 40,
            },
          },
          content: {
            margin: "8px 0",
            "&.Mui-expanded": {
              margin: "8px 0",
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: "8px 12px 12px",
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
          },
          separator: {
            marginLeft: 6,
            marginRight: 6,
          },
        },
      },
      MuiStepper: {
        styleOverrides: {
          root: {
            padding: 16,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: "0.8125rem",
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            "& .MuiSnackbarContent-root": {
              borderRadius: BORDER_RADIUS.sm,
              padding: "4px 12px",
              fontSize: "0.8125rem",
            },
          },
        },
      },
      MuiCircularProgress: {
        defaultProps: {
          size: 20,
          thickness: 4,
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.xs,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: mode === "dark" ? "#334155" : "#e2e8f0",
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: BORDER_RADIUS.sm,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    spacing: 8,
    transitions: {
      duration: {
        shortest: ANIMATION.fastest,
        shorter: ANIMATION.fast,
        short: 200,
        standard: ANIMATION.normal,
        complex: 300,
        enteringScreen: 200,
        leavingScreen: 150,
      },
      easing: {
        easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
        easeIn: "cubic-bezier(0.4, 0, 1, 1)",
        sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
      },
    },
    zIndex: {
      mobileStepper: 1000,
      fab: 1050,
      speedDial: 1050,
      appBar: Z_INDEX.appBar,
      drawer: Z_INDEX.drawer,
      modal: Z_INDEX.modal,
      snackbar: Z_INDEX.snackbar,
      tooltip: Z_INDEX.tooltip,
    },
  });
};

// Default light theme
const theme = createAppTheme("light");

export default theme;
